<script lang="ts">
import { ref } from 'vue';

export const facilityStatusUpdateTrigger = ref(0);
</script>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { watchAndFetch } from '../../../cross-app-frontend/watch-and-fetch';
import { actions } from '@/services/actions';
import { slugFromName } from '../../../tri-shared/services/formatting';
import { Popover, PopoverContent, PopoverTrigger } from '../../../cross-app-frontend/popover';
import { Button } from '../../../cross-app-frontend/button';
import type { FacilityID } from '../../../tri-shared/types/db';
import { useFacilityWorkflowStatusAndNavigation } from '@/services/facility-workflow-status-and-navigation';

const props = defineProps<{
  facilityID: FacilityID;
  year: number;
}>();

// Fetch facility status data - include the global status update trigger to make it reactive
const { loading, response: statusLoaded } = watchAndFetch(
  computed(() => ({
    facilityID: props.facilityID,
    year: props.year,
    trigger: facilityStatusUpdateTrigger.value,
  })),
  null,
  async ({ facilityID, year }) => {
    return actions.facilities.status({ facilityID, year });
  },
);

const facilityPermissions = ref<{
  hasUpdateBaseFacts: boolean;
  hasUpdateFacility: boolean;
  hasAdministerFacility: boolean;
  hasManageUsers: boolean;
} | null>(null);

watch(
  () => props.facilityID,
  async (facilityID) => {
    try {
      const summary = await actions.users.facilityPermissionSummary({ facilityID });
      facilityPermissions.value = summary as {
        hasUpdateBaseFacts: boolean;
        hasUpdateFacility: boolean;
        hasAdministerFacility: boolean;
        hasManageUsers: boolean;
      };
    } catch (e) {
      console.error('Failed to load facility permission summary', e);
      facilityPermissions.value = null;
    }
  },
  { immediate: true },
);

const permissionsForWorkflow = computed(() => {
  if (!facilityPermissions.value) return null;
  return {
    hasUpdateBaseFacts: facilityPermissions.value.hasUpdateBaseFacts,
    hasUpdateFacility: facilityPermissions.value.hasUpdateFacility,
    hasAdministerFacility: facilityPermissions.value.hasAdministerFacility,
  };
});

const stages = useFacilityWorkflowStatusAndNavigation(statusLoaded, permissionsForWorkflow);

const isAdmin = computed(() => {
  return !!facilityPermissions.value?.hasAdministerFacility;
});

const visibleStages = computed(() => {
  return stages.value.filter((stage) => isAdmin.value || stage.route !== 'Facility Form R Submit');
});

// Calculate overall percentage
const overallPercentage = computed(() => {
  if (loading.value) {
    // During loading, preserve the last known percentage or show 0
    const lastStages = stages.value;
    if (lastStages.length === 0) return 0;
    const completed = lastStages.filter((s) => s.isDone).length;
    return Math.round((completed / lastStages.length) * 100);
  }

  if (stages.value.length === 0) return 0;
  const completed = stages.value.filter((s) => s.isDone).length;
  return Math.round((completed / stages.value.length) * 100);
});

// Calculate stroke offset for the progress circle
const circleCircumference = 2 * Math.PI * 20; // radius = 20
const strokeDashoffset = computed(() => {
  const progress = overallPercentage.value / 100;
  // Ensure we always show at least a small portion of the circle
  const minProgress = 0.02; // 2% minimum to make it visible
  const effectiveProgress = Math.max(progress, minProgress);
  return circleCircumference * (1 - effectiveProgress);
});

// Control popover open state
const popoverOpen = ref(false);
</script>

<template>
  <div v-if="statusLoaded || loading" class="relative">
    <Popover v-model:open="popoverOpen">
      <PopoverTrigger asChild>
        <div class="group relative h-12 w-12 cursor-pointer">
          <!-- Progress Border SVG -->
          <svg class="absolute inset-0 h-12 w-12 -rotate-90 transform" viewBox="0 0 48 48">
            <!-- Background circle -->
            <circle cx="24" cy="24" r="20" stroke="#e5e7eb" stroke-width="4" fill="none" />
            <!-- Progress circle (the border that fills) -->
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke="#3fa76f"
              stroke-width="6"
              fill="none"
              :stroke-dasharray="circleCircumference"
              :stroke-dashoffset="strokeDashoffset"
              stroke-linecap="round"
              class="transition-all duration-300 ease-in-out"
            />
          </svg>

          <!-- Inner content button -->
          <div
            class="absolute inset-1 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-gray-300 bg-white p-0 shadow-xs transition-all duration-200 hover:bg-gray-50"
            :class="{ 'opacity-75': loading }"
            :title="`TRI Workflow Progress: ${overallPercentage}% complete`"
          >
            <!-- Loading spinner when updating -->
            <i v-if="loading" class="fa fa-spin fa-spinner-third text-xs text-gray-500"></i>
            <!-- Percentage text in center -->
            <span v-else class="text-xs font-bold text-gray-700"> {{ overallPercentage }}% </span>
          </div>
        </div>
      </PopoverTrigger>

      <PopoverContent class="w-80 border-0 p-0 shadow-lg" align="end">
        <div class="rounded-lg bg-white p-4">
          <div class="mb-4 border-b border-gray-100 pb-3">
            <h3 class="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <div class="h-2 w-2 rounded-full bg-blue-500"></div>
              TRI Workflow Progress
            </h3>
            <p class="mt-1 text-sm text-gray-600">
              {{ stages.filter((s) => s.isDone).length }} of {{ stages.length }} steps completed
              <span class="font-medium text-blue-600">({{ overallPercentage }}%)</span>
            </p>
          </div>

          <div class="space-y-3">
            <RouterLink
              v-for="stage of visibleStages"
              :key="stage.stage"
              class="flex cursor-pointer items-center gap-3 rounded-lg border border-transparent p-3 transition-all duration-200 hover:border-blue-100 hover:bg-blue-50"
              :to="{
                name: stage.route,
                params: {
                  facilityID,
                  facilitySlug: slugFromName(statusLoaded?.name ?? ''),
                  year,
                },
              }"
            >
              <!-- Stage Icon -->
              <div
                class="flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200"
                :class="
                  stage.isDone ? 'bg-brand-green ring-brand-green text-white ring-2' : 'bg-gray-100 text-gray-400'
                "
              >
                <i class="fa-light text-sm" :class="stage.icon"></i>
              </div>

              <!-- Stage Info -->
              <div class="min-w-0 flex-1">
                <h4 class="truncate text-sm font-medium text-gray-900">
                  {{ stage.stage }}
                </h4>
                <p class="truncate text-xs text-gray-500">
                  {{ stage.briefDetails }}
                </p>
              </div>

              <!-- Arrow Icon -->
              <div class="shrink-0">
                <i
                  class="fa-light fa-chevron-right text-sm text-gray-400 transition-transform duration-200 group-hover:translate-x-1"
                ></i>
              </div>
            </RouterLink>
          </div>

          <!-- View Full Status Button -->
          <div class="mt-4 border-t border-gray-100 pt-3">
            <Button
              variant="outline"
              size="sm"
              class="w-full transition-colors duration-200 hover:border-blue-200 hover:bg-blue-50"
              :as="RouterLink"
              :to="{
                name: 'Facility Status',
                params: {
                  facilityID,
                  facilitySlug: slugFromName(statusLoaded?.name ?? ''),
                  year,
                },
              }"
            >
              <i class="fa-light fa-chart-line mr-2 text-blue-600"></i>
              <span class="font-medium text-blue-600">View Full Status</span>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>

<style scoped>
/* Ensure smooth animations */
circle {
  transition: stroke-dashoffset 0.3s ease-in-out;
}
</style>
